import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AssessorOverview } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Badge, Card, EmptyState, LoadingPane, PageHeader, Table } from '../../components/ui.js';
import '../admin/platform.css';

export function AssessorOverviewPage() {
  const { org } = useOrg();
  const isAssessor = org.role === 'assessor';

  const overview = useQuery({
    queryKey: ['assessor-overview'],
    queryFn: () => apiFetch<AssessorOverview>('/assessor/overview'),
    enabled: isAssessor,
  });

  if (!isAssessor) {
    return (
      <EmptyState
        title="Assessors only"
        message="This area is for external assessors assigned to review organisations."
      />
    );
  }
  if (overview.isLoading) return <LoadingPane label="Loading your assessor overview" />;
  const o = overview.data;

  return (
    <div>
      <PageHeader
        title="Assessor overview"
        subtitle="Your sign-off queue and activity across every organisation you assess"
      />

      {o && (
        <>
          <div className="plat-tiles">
            <div className="plat-tile accent">
              <div className="value">{o.awaitingAssessment.toLocaleString()}</div>
              <div className="label">Awaiting your sign-off</div>
              <div className="sub">finalised responses to assess</div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.orgCount}</div>
              <div className="label">Organisations</div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.assessmentCount}</div>
              <div className="label">Assessments in scope</div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.assessed.toLocaleString()}</div>
              <div className="label">Responses assessed</div>
            </div>
            <div className="plat-tile">
              <div className="value">{o.openComments.toLocaleString()}</div>
              <div className="label">Your open comments</div>
            </div>
          </div>

          <Card title="By organisation">
            {o.perOrg.length === 0 ? (
              <EmptyState
                title="No assignments yet"
                message="Once an organisation assigns you as their assessor, their assessments appear here."
              />
            ) : (
              <Table
                head={
                  <>
                    <th>Organisation</th>
                    <th>Assessments</th>
                    <th>Awaiting you</th>
                    <th />
                  </>
                }
              >
                {o.perOrg.map((r) => (
                  <tr key={r.orgId}>
                    <td style={{ fontWeight: 600 }}>{r.orgName}</td>
                    <td>{r.assessments}</td>
                    <td>
                      {r.awaitingAssessment > 0 ? (
                        <Badge tone="warning">{r.awaitingAssessment}</Badge>
                      ) : (
                        <span style={{ color: 'var(--color-text-faint)' }}>0</span>
                      )}
                    </td>
                    <td>
                      <Link to="/assessor/reviews">Review →</Link>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
